import { memo, useMemo } from "react";
import { format, isBefore, addDays } from "date-fns";
import { nl } from "date-fns/locale";
import {
  Calendar,
  CheckCircle,
  Warning,
  Calendar as CalendarIcon,
} from "@phosphor-icons/react";
import { FileLogStatus } from "shared/types/file";
import { DueDiligenceUploadCell } from "./DueDiligenceUploadCell";
import type { DocumentLog, ResourceRow } from "./types";
import { isLabelRequired } from "./types";

interface Props {
  resource: ResourceRow;
  label: string;
  isBuilding: boolean;
  required: Record<string, Record<string, boolean>> | undefined;
  onCellClick: (resource: ResourceRow, label: string, isBuilding: boolean) => void;
}

interface CellStatus {
  colorClass: string;
  text: string;
  isError: boolean;
}

const pickLatestLog = (logs: DocumentLog[]): DocumentLog => {
  return logs.reduce((latest, current) => {
    const latestDate = latest.documentDate ? new Date(latest.documentDate as string) : new Date(0);
    const currentDate = current.documentDate ? new Date(current.documentDate as string) : new Date(0);

    if (currentDate.getTime() === latestDate.getTime()) {
      const latestCreatedAt = latest.createdAt ? new Date(latest.createdAt as string) : new Date(0);
      const currentCreatedAt = current.createdAt ? new Date(current.createdAt as string) : new Date(0);
      return currentCreatedAt > latestCreatedAt ? current : latest;
    }

    return currentDate > latestDate ? current : latest;
  }, logs[0]);
};

const getCellStatus = (
  log: DocumentLog,
  isExpired: boolean,
  isExpiringSoon: boolean,
): CellStatus => {
  if (isExpired) {
    return { colorClass: "bg-brick-primary", text: "Verlopen", isError: true };
  }
  if (isExpiringSoon) {
    return { colorClass: "bg-[#F59E0B]", text: "Verloopt binnenkort", isError: false };
  }
  if (log.status === FileLogStatus.NOT_OK) {
    return { colorClass: "bg-brick-primary", text: "Niet conform", isError: true };
  }
  if (log.status === FileLogStatus.NOT_REQUIRED) {
    return { colorClass: "bg-status-good", text: "N.V.T.", isError: false };
  }
  if (log.status && log.status !== FileLogStatus.OK) {
    return {
      colorClass: "bg-neutral-secondary-mid",
      text: String(log.status) || "Onbekend",
      isError: false,
    };
  }
  return { colorClass: "bg-status-good", text: "Conform", isError: false };
};

const DueDiligenceCellInner = ({
  resource,
  label,
  isBuilding,
  required,
  onCellClick,
}: Props) => {
  const labelRequired = isLabelRequired(required, resource.id, label);

  const latestLog = useMemo(() => {
    const logs = (resource.labels[label] || []) as DocumentLog[];
    return logs.length > 0 ? pickLatestLog(logs) : null;
  }, [resource.labels, label]);

  if (!labelRequired) {
    return (
      <div className="flex items-center gap-2">
        <div className="bg-status-good rounded-md p-1 shadow-sm">
          <CheckCircle size={14} className="text-white" weight="fill" />
        </div>
        <span className="text-xs font-semibold">Niet vereist</span>
      </div>
    );
  }

  if (!latestLog) {
    return <DueDiligenceUploadCell onClick={() => onCellClick(resource, label, isBuilding)} />;
  }

  const expirationDate = latestLog.expirationDate ? new Date(latestLog.expirationDate as string) : null;
  const today = new Date();
  const isExpired = !!expirationDate && isBefore(expirationDate, today);
  const isExpiringSoon =
    !!expirationDate && !isExpired && isBefore(expirationDate, addDays(today, 30));

  const status = getCellStatus(latestLog, isExpired, isExpiringSoon);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <div className={`rounded-md p-1 ${status.colorClass} shadow-sm`}>
          {status.isError ? (
            <Warning size={14} className="text-white" weight="fill" />
          ) : (
            <CheckCircle size={14} className="text-white" weight="fill" />
          )}
        </div>
        <span className="text-xs font-semibold">{status.text}</span>
      </div>

      {(latestLog.documentDate || expirationDate) && (
        <div className="flex flex-col gap-1">
          {latestLog.documentDate && (
            <div className="text-neutral-primary/80 bg-moss-primary/5 border-moss-primary/10 flex w-fit items-center gap-1 rounded border px-1.5 py-0.5 text-[11px]">
              <Calendar size={11} weight="bold" className="text-moss-primary/70" />
              <span className="font-medium">
                {format(new Date(latestLog.documentDate as string), "dd MMM yyyy", { locale: nl })}
              </span>
            </div>
          )}
          {expirationDate && (
            <div className="text-neutral-primary/80 bg-brick-secondary-fine/40 border-brick-primary/10 flex w-fit items-center gap-1 rounded border px-1.5 py-0.5 text-[11px]">
              <CalendarIcon size={11} weight="bold" className="text-brick-primary/70" />
              <span className="font-medium">
                {format(expirationDate, "dd MMM yyyy", { locale: nl })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DueDiligenceCell = memo(DueDiligenceCellInner);
